import { useEffect, useRef, useState, type ReactNode } from 'react';
import { IconArrowUpRight, IconCheck, IconExternalLink, IconX } from '@tabler/icons-react';
import { conn } from '../net/connection';
import { machineName, toast, useStore, type OpenLink } from '../store';
import { dismissOpenLink, hereHref, isSignin, markOpenedOnMachine, openHere, setLinksSetting, shortLink } from './openLinkStore';
import { Button, Ic, type TablerIcon } from './ds';
import { copyText } from './sheets';
import { useActions } from './Workspace';

/** How long a card about a link already open in the machine's browser stays (nothing needs a click). */
const MACHINE_CARD_MS = 8000;

/**
 * A link a program in the session asked to open (one toast in the pane's stack, PaneToasts):
 * waiting for a click here, already open in the machine's browser, or a sign-in that went
 * through there. See openLinkStore for where each goes.
 */
export function OpenLinkCard({ link }: { link: OpenLink }) {
  if (link.done) return <SignedIn link={link} />;
  return link.target === 'machine' ? <OnMachine link={link} /> : <WaitingHere link={link} />;
}

/** The card's frame: icon, text (with the full link as its title) and ✕ on the first line, the actions under them. */
function Card({ link, state, icon, children, actions, hover }: { link: OpenLink; state: string; icon: TablerIcon; children: ReactNode; actions?: ReactNode; hover?: (on: boolean) => void }) {
  return (
    <div
      className="toast-card open-link"
      role="status"
      data-testid="open-link-card"
      data-target={link.target}
      data-reason={link.reason}
      data-state={state}
      data-url={link.url}
      onPointerEnter={() => hover?.(true)}
      onPointerLeave={() => hover?.(false)}
    >
      <div className="open-link-row">
        <span className="open-link-icon">
          <Ic icon={icon} />
        </span>
        <span className="open-link-text" title={link.url}>
          {children}
        </span>
        <Button variant="icon" radius={6} className="open-link-x" aria-label="Dismiss" data-testid="open-link-dismiss" onClick={() => dismissOpenLink(link.id)}>
          <Ic icon={IconX} size={14} />
        </Button>
      </div>
      {actions && <div className="open-link-actions">{actions}</div>}
    </div>
  );
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
     
      className="open-link-quiet"
      data-testid="open-link-copy"
      onClick={async () => {
        if (await copyText(text)) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }
      }}
    >
      {copied ? 'Copied' : label}
    </Button>
  );
}

/** Goes away by itself after `ms`, but not while the pointer is on it. Returns the hover setter. */
function useAutoDismiss(id: string, ms: number | null): (on: boolean) => void {
  const hovered = useRef(false);
  const [lapse, setLapse] = useState(0);
  useEffect(() => {
    if (ms === null) return;
    const t = setTimeout(() => {
      if (hovered.current) setLapse((n) => n + 1);
      else dismissOpenLink(id);
    }, ms);
    return () => clearTimeout(t);
  }, [id, ms, lapse]);
  return (on) => (hovered.current = on);
}

/** A link this browser couldn't open by itself (no recent key or click): Open, or the machine's browser. */
function WaitingHere({ link }: { link: OpenLink }) {
  const [busy, setBusy] = useState(false);
  const machine = machineName();
  const openOnMachine = async () => {
    setBusy(true);
    try {
      await conn.control('open.machine', { url: link.url });
      markOpenedOnMachine(link.id);
    } catch (e) {
      toast({
        title: `Couldn’t open it in ${machine}’s browser`,
        body: e instanceof Error ? e.message : String(e),
        kind: 'error',
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card
      link={link}
      state="waiting"
      icon={IconExternalLink}
      actions={
        <>
          <Button
            variant="primary"
           
            data-testid="open-link-open"
            onClick={() => {
              openHere(link, true);
              dismissOpenLink(link.id);
            }}
          >
            Open
          </Button>
          <Button data-testid="open-link-machine" disabled={busy} onClick={() => void openOnMachine()}>
            {busy ? 'Opening…' : `Open in ${machine}’s browser`}
          </Button>
          <CopyButton text={hereHref(link)} />
        </>
      }
    >
      <span className="open-link-app">{link.app || 'A program'}</span> wants to open <b>{shortLink(hereHref(link))}</b>
    </Card>
  );
}

/**
 * Open in the machine's browser: the card is the notice, with Show desktop (the desktop doesn't
 * come up by itself) and one action that does otherwise this time. A sign-in's card stays until
 * it's through; the others go after a few seconds.
 */
function OnMachine({ link }: { link: OpenLink }) {
  const a = useActions();
  const machine = useStore((s) => s.me?.machine?.id) || 'the cloud machine';
  const signin = isSignin(link);
  const hover = useAutoDismiss(link.id, signin ? null : MACHINE_CARD_MS);
  const setting = useStore((s) => s.me?.settings.links ?? 'auto');
  const server = link.reason === 'server';
  const here = (label: string) => (
    <Button
      data-testid="open-link-here"
      onClick={() => {
        openHere(link, true);
        dismissOpenLink(link.id);
      }}
    >
      {label}
    </Button>
  );
  return (
    <Card
      link={link}
      state="machine"
      icon={IconArrowUpRight}
      hover={hover}
      actions={
        <>
          <Button variant="primary" data-testid="open-link-desktop" onClick={a.openDesktop}>
            Show desktop
          </Button>
          {signin ? (
            <CopyButton text={link.url} label="Copy link" />
          ) : (
            here(server ? 'Open preview on this computer' : 'Open on this computer instead')
          )}
          {/* "Always" when the setting is what sends these here: dev servers but for "device", links only for "machine". */}
          {!signin && (server ? setting !== 'device' : setting === 'machine') && (
            <Button className="open-link-quiet" data-testid="open-link-always" onClick={() => void setLinksSetting(server ? 'device' : 'auto').then((ok) => ok && dismissOpenLink(link.id))}>
              Always open on this computer
            </Button>
          )}
        </>
      }
    >
      <b>{shortLink(link.url)}</b> opened in {machine}’s browser
    </Card>
  );
}

function SignedIn({ link }: { link: OpenLink }) {
  let site = link.url;
  try {
    site = new URL(link.url).host;
  } catch {
    /* as is */
  }
  return (
    <Card link={link} state="done" icon={IconCheck}>
      Signed in: <b>{site}</b>
    </Card>
  );
}
